import type { ReactNode, Ref } from 'react';
import styles from './Shell.module.css';

/**
 * Figma: `Shell` (206:4699) — the application frame. One `Sidebar` down the
 * left, a `Top Navigation Bar` and a `Filters Bar` stacked across the top of the
 * remaining column, and the page itself below them.
 *
 * The frame carries no annotation and no variant properties: it is three
 * instances on a `bg/primary` fill, positioned. So this is a layout, not a
 * fourth organism that re-declares the other three — the slots take elements
 * rather than each organism's props, which is what lets a caller swap the
 * filters bar for something else, or drop it entirely, without this component
 * growing a prop per case. `Sidebar`, `TopNavigationBar` and `FiltersBar`
 * already own everything inside their own boxes.
 *
 * Two Figma layers are hidden on the canvas — `Section header` and
 * `Section footer` — and are not rendered here. They belong to whatever page
 * fills `children`, not to the shell.
 */
export type ShellProps = {
  /** Figma's `Sidebar` instance. Rendered as-is; it supplies its own 200px. */
  sidebar?: ReactNode;
  /** Figma's `Top Navigation Bar` instance. */
  header?: ReactNode;
  /** Figma's `Filters Bar` instance. Omit for a page that does not filter. */
  filters?: ReactNode;
  /** The page. Blank in Figma, which mocks the chrome only. */
  children?: ReactNode;
  /**
   * A floating strip pinned to the bottom of the page area: a custom report's
   * `Floating Actions  Row` (2161:34964), whose frame pins it 16 above the
   * bottom edge (vertical constraint `MAX`) and centres it on the area right of
   * the sidebar. Not part of Figma's `Shell`, which mocks no page content.
   *
   * It is a slot here rather than something a page positions itself, because
   * only the shell can put it right. It is the scroller's last child and
   * `position: sticky` against the scroller, which gives three things a page
   * cannot reach from inside `.content`:
   *  - it holds the bottom of the window however short the page is, since
   *    `.content` grows to fill the height above it;
   *  - it does not move at the end of the scroll, since its natural place and
   *    its pinned place are then the same point;
   *  - it takes its own height in flow, so the last of the page scrolls clear
   *    of it instead of ending underneath it.
   *
   * A page-level sticky inside `.content` got the first and second wrong: its
   * containing block ended at `.content`'s padding, so it floated up with a
   * short page and was nudged 8px in the last pixels of a long one.
   */
  dock?: ReactNode;
  /**
   * Accessible name for the `<main>` landmark. Not in Figma — with a banner and
   * a nav landmark on the page, the main region is the one a screen-reader user
   * jumps to, and on a shell that hosts many pages it should say which.
   */
  label?: string;
  className?: string;
  /**
   * The scroll container (`<main>`). Not in Figma — the app resets its scroll
   * on navigation, which `window` scrolling cannot do because the page scrolls
   * here, not on the document.
   */
  mainRef?: Ref<HTMLElement>;
};

export function Shell({ sidebar, header, filters, children, dock, label, className, mainRef }: ShellProps) {
  return (
    <div className={[styles.shell, className].filter(Boolean).join(' ')}>
      {sidebar}

      {/*
       * `<main>` rather than a div, and it wraps the bars as well as the page:
       * `TopNavigationBar` renders its own `<header>` banner and `Sidebar` its
       * own `<nav>`, so the region left over is exactly this column. Not in
       * Figma, which has no concept of a landmark.
       */}
      <main ref={mainRef} className={styles.main} aria-label={label}>
        {(header || filters) && (
          <div className={styles.bars}>
            {header}
            {filters}
          </div>
        )}

        <div className={styles.content}>{children}</div>

        {dock && <div className={styles.dock}>{dock}</div>}
      </main>
    </div>
  );
}
